import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import CategoryDropdown from "../components/CategoryDropdown";
import SingleWinPlanListRow from "../components/SingleWinPlanListRow";
import { getMessages } from "../API/message";
import getName from "../util/getName";
import { AppContext } from "../util/AppContext";
import MessageDetailsModal from "../components/MessageDetailsModal";
import MessageTypes from "../util/messageTypes";
import { ProductsModal, ProductsModalButton } from "../components/ProductsModal";
import { getConfigValue } from "../util/getConfigValue";
import PrintOptionDropdown from "../components/printOptions/printOptionDropdown";
import { useErrorBoundary } from "react-error-boundary";
import useScrollPosition from "../hooks/useScrollPosition";
import { useRoutingValidation } from "../util/useRoutingValidation";
import { FavoriteDesignation, updateHcpAppState } from "../components/FavoriteDesignation";
import { useBreadcrumbs } from "../util/BreadcrumbContext";
import CreateSalesAidStepProgressBar from "../components/CreateSalesAidStepProgressBar";
import { SalesAidStepIds } from "../util/salesAidNavigation";
import PageTitle from "../components/PageTitle";

function WinPlanListPage() {
    const [appState, setAppState] = useContext(AppContext);
    const [showProductsModal, setShowProductsModal] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(appState?.currentProduct);
    const [productImageSource, setProductImageSource] = useState(appState?.currentProduct?.imagePath);
    const [prescriberId] = useState(appState.prescriber?.id);
    const [messageCode, setMessageCode] = useState(appState.category?.messageCode);
    const [categories, setCategories] = useState([]);
    const [pageCategory, setPageCategory] = useState(appState.category);
    const [plans, setPlans] = useState(appState.plans);
    const { showBoundary } = useErrorBoundary();
    const [isConfigMenuVisible, setIsConfigMenuVisible] = useState(false);
    const [showMessageDetailsModal, setShowMessageDetailsModal] = useState(false);
    const configMenuRef = useRef(null);
    const configMenuContentRef = useRef(null);

    const isImprovedAccessStatic = true;

    const navigate = useNavigate();
    const scrollPosition = useScrollPosition();
    const { setBreadcrumbs } = useBreadcrumbs();

    useEffect(() => {
        setBreadcrumbs([
            { label: getConfigValue(appState.config?.Focus?.Header, appState)?.Value, path: "/FocusPage" }
        ]);
    }, [setBreadcrumbs]);

    useRoutingValidation();

    useEffect(() => {
        if (categories === null || categories.length === 0) {
            getMessageCategories();
        }
    }, []);

    useEffect(() => {
        async function loadMessages() {
            getMessages(messageCode, prescriberId, appState.currentProduct.id, appState.category.messageTypeId, false, appState?.prescriberType)
                .then((responseJson) => {
                    const responsePlans = responseJson.dynamicTable || [];
                    const responseMainMessage = responseJson.prescriberMessage.mainMessage || '';
                    setAppState({ ...appState, plans: responsePlans, mainMessage: responseMainMessage });
                    setPlans(responsePlans);
                })
                .catch(e => showBoundary(e));
        }
        if (messageCode && prescriberId) {
            loadMessages();
        }
        // eslint-disable-next-line
    }, [messageCode, prescriberId, appState.category.messageTypeId])

    function getMessageCategories() {
        if (appState.products.length > 0) {
            const categories = appState.products.find((x) => {
                return x.productId === appState.currentProduct.clientProductId;
            })
            setCategories(categories.messages);
        }
    }

    function updateSelectMessageCategory(pageCategory) {
        setMessageCode(pageCategory.messageCode);
        setAppState({
            ...appState,
            category: pageCategory,
            messageCode: pageCategory.messageCode,
        });
    }

    function onProductClick(product) {
        setAppState({ ...appState, currentProduct: product });
        setCurrentProduct(product);
        setShowProductsModal(false);
        setProductImageSource(product.imagePath)
        navigate('/MessageSelectionPage');
    }

    function closeOpenMenus(event) {
        // click outside of menus and content menus = close menus
        if (configMenuRef.current && !configMenuRef.current.contains(event.target) && !configMenuContentRef.current.contains(event.target) && isConfigMenuVisible) {
            setIsConfigMenuVisible(false);
        }
    }

    function onDetailsClick() {
        setShowMessageDetailsModal(true);
    }

    function navigateToPage(category) {
        if (category.messageType === MessageTypes.ImprovedAccess) {
            navigate('/WinPlanListPage');
        } else {
            navigate('/PlanListPage');
        }
    }

    function onDetailsCategoryClick(category) {
        setPageCategory(category);
        setShowMessageDetailsModal(false);
        updateSelectMessageCategory(category);
        navigateToPage(category);
    }

    function planListHeaderSticky() {
        const planListHeader = document.getElementById('plan-list-header');
        if (planListHeader) {
            if (scrollPosition > 150) {
                planListHeader.classList.add("plan-list-header-sticky");
                planListHeader.classList.add("border-bottom");
                document.getElementById('plan-list-header-spacer').classList.add('d-block');
                document.getElementById('plan-list-header-spacer').classList.remove('d-none');
            }
            else if (planListHeader.classList.contains("plan-list-header-sticky")) {
                planListHeader.classList.remove("plan-list-header-sticky");
                planListHeader.classList.remove("border-bottom");
                document.getElementById('plan-list-header-spacer').classList.remove('d-block');
                document.getElementById('plan-list-header-spacer').classList.add('d-none');
            }
        }
    }

    document.addEventListener('mousedown', closeOpenMenus);

    return (
        <>
            <div>
                <CreateSalesAidStepProgressBar currentStepId={SalesAidStepIds.CONFIGUREMESSAGE} focusType={appState?.focus} />

                <div
                    className="container-fluid pt-3 pt-md-4 pb-1 color-bg-bodybg z-2"
                    id="plan-list-header"
                    onScroll={planListHeaderSticky()}
                >
                    <div className="container-xl">
                        <div className='row d-flex align-items-md-center mb-2 mb-lg-4'>
                            <div className='col-12 col-md-6 order-2 order-md-1'>
                                <div className="mt-4 mt-md-0">
                                    <PageTitle title={getConfigValue(appState.config?.PlanList?.Title, appState)?.Value || null} />
                                </div>
                                <div className="row justify-content-end show-sticky-only">
                                    <div className="row justify-content-start">
                                        <div className="col-6 col-md-auto type-14 text-md-start"><div className="color-grey-d1">{getConfigValue(appState.config?.PlanList?.ProductLabel, appState)?.Value}</div>{appState.currentProduct.name}</div>
                                        <div className="col-6 col-md-auto type-14 text-md-start"><div className="color-grey-d1">{getConfigValue(appState.config?.PlanList?.MessageTypeLabel, appState)?.Value}</div>{pageCategory.category}</div>
                                    </div>
                                </div>
                            </div>
                            <div className='col-12 col-md-6 order-1 order-md-2'>
                                <div className='row justify-content-end'>
                                    <div className='col-6 col-md-auto type-14 text-md-end favorite-hcp'>
                                        <div className='color-grey-d1'>
                                            {getConfigValue(appState.config?.PlanList?.ProviderLabel, appState)?.Value}
                                        </div>
                                        <FavoriteDesignation hcp={appState.prescriber} updateCallback={() => updateHcpAppState(appState.prescriber, setAppState)} ></FavoriteDesignation>{getName(appState)}
                                    </div>
                                    <div className='col-6 col-md-auto type-14 text-md-end'>
                                        <div className='color-grey-d1'>
                                            {getConfigValue(appState.config?.PlanList?.TerritoryLabel, appState)?.Value}
                                        </div>
                                        {appState.territory?.name}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="my-8 d-none" id="plan-list-header-spacer"></div>

                {/* <!-- Dropdowns row --> */}

                <div className="container-xl">

                    <div className="row">
                        {/* This section will be replaced with the config data to pull in the brands */}
                        <div className="col-12 col-md-4">
                            <ProductsModalButton
                                setShowProductsModal={setShowProductsModal}
                                productImageSource={productImageSource}
                                productName={currentProduct?.productName}
                            ></ProductsModalButton>
                        </div>

                        <div className="col-12 col-md-4 mt-4 mt-md-0">

                            <CategoryDropdown
                                pageCategory={pageCategory}
                                onDetailsClick={onDetailsClick}
                            />

                        </div>

                        <div className="col-12 col-md-4 mt-4 mt-md-0">
                            <PrintOptionDropdown
                                isImprovedAccessStatic={isImprovedAccessStatic}
                                isConfigMenuVisible={isConfigMenuVisible}
                                setIsConfigMenuVisible={setIsConfigMenuVisible}
                                configMenuContentRef={configMenuContentRef}
                                configMenuRef={configMenuRef}
                                isDisabled={isImprovedAccessStatic}
                            />
                        </div>
                    </div>

                    {/* <!-- Instructions row. May depend on message type --> */}

                    <div className="row my-5 my-lg-1">
                        <div className="col-12">
                            <p>
                                {getConfigValue(appState.config?.WinPlanList?.CallToAction, appState)?.Value}
                            </p>
                        </div>
                    </div>

                    {/* <!-- Plan list --> */}

                    <div className="container-xl">

                        <div className="row pd-plan-table-header align-items-lg-center d-none d-lg-flex py-3">

                            {/* <!-- Fixed column headers --> */}

                            <div className="pd-plan-col col-lg-3">
                                <div className="row align-items-lg-center">
                                    <div className="col-12">{getConfigValue(appState?.config?.SingleWinPlanListRow?.Channel?.Label, appState)?.Value || null}</div>
                                </div>
                            </div>

                            <div className="pd-plan-col col-lg-4">
                                <div className="row align-items-lg-center">
                                    <div className="col-12">{getConfigValue(appState?.config?.SingleWinPlanListRow?.Plan?.Label, appState)?.Value || null}</div>
                                </div>
                            </div>

                            {/* <!-- Variable column headers --> */}

                            <div className="pd-plan-col col-lg-3">
                                <div className="row align-items-lg-center">
                                    <div className="col-12">{getConfigValue(appState?.config?.SingleWinPlanListRow?.Status?.Label, appState)?.Value || null}</div>
                                </div>
                            </div>

                            <div className="pd-plan-col col-lg-2">
                                <div className="row align-items-lg-center">
                                    <div className="col-12"></div>
                                </div>
                            </div>
                        </div>

                        {plans?.rows &&
                            plans.rows
                                .filter((plan) => plan.data.isWin === true)
                                .map((plan, index) => {
                                    return (
                                        <SingleWinPlanListRow key={index} plan={plan.data} index={index} />
                                    );
                                })}

                    </div>
                    <div className="my-8"></div>
                </div>
            </div>

            <div className='button-bottom-container-sticky-low-z'>
                <div className='container-xl'>
                    <div className='row py-3'>
                        <div className='col-5'>
                            <div className='d-flex flex-column flex-lg-row'>
                                <button className="btn btn-outline-primary btn-lg flex-fill w-100 disabled">Preview</button>
                                <div className='flex-fill w-100 ms-lg-3 pe-3'></div>
                            </div>
                        </div>
                        <div className='col-7'>
                            <div className='d-flex flex-md-row-reverse'>
                                <button className="btn btn-success btn-lg mb-lg-0 ms-lg-3 flex-fill w-100 ml-1 disabled" type="button">Generate</button>
                                <Link
                                    to="/MessageSelectionPage"
                                    className="btn btn-secondary btn-lg pe-7 flex-fill w-100">
                                    Back
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <MessageDetailsModal
                show={showMessageDetailsModal}
                setShow={setShowMessageDetailsModal}
                categories={categories}
                onDetailsCategoryClick={onDetailsCategoryClick}
            />
            <ProductsModal
                show={showProductsModal}
                setShow={setShowProductsModal}
                onProductClick={onProductClick}
                onlySellSheetProducts={true}
            />
        </>
    );
}

export default WinPlanListPage;
